import { useMemo, useState } from 'react';

import { useApp } from '../../../../app/AppContext.jsx';
import { useProduct } from '../../index.js';

import { useI18n } from '../../../../shared/i18n/useI18n.js';
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js';

import {
	parsePricesVisibility,
	formatCurrency,
} from '../../../../shared/utils/priceUtils.js';

import { buildPricingData } from '../../utils/buildPricingData.js';

import { MonetaryObligationsModal } from './MonetaryObligationsModal.jsx';
import { PriceHistoryModal } from './PriceHistoryModal.jsx';
import { SupplementPickerModal } from './SupplementPickerModal.jsx';

import { AddToList } from '../../../../shared/ui/icons/index.js';
import { Button } from '../../../../shared/ui/Button/index.js';
import { Car } from '../../../../shared/ui/icons/Car.jsx';
import { ChevronUp } from '../../../../shared/ui/icons/index.js';
import { Close } from '../../../../shared/ui/icons/index.js';
import { Documents } from '../../../../shared/ui/icons/Documents.jsx';
import { History } from '../../../../shared/ui/icons/History.jsx';
import { InfoTooltip } from '../../../../shared/ui/InfoTooltip/InfoTooltip.jsx';

import styles from './PricingPane.module.scss';

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * AdditionalInfoButtons — icon row for price history, supplement picker, monetary obligations.
 * Matches the legacy AdditionalInfoIcons pattern: icons visible when their respective capability is on.
 */
function AdditionalInfoButtons({ capabilities, featureFlows, t }) {
	const { hasPriceHistory, hasSupplements, hasMonetaryObligations } =
		capabilities;
	const { openPicker } = featureFlows.supplements;
	const { openPriceHistory } = featureFlows.history;
	const { openObligations } = featureFlows.obligations;

	if (!hasPriceHistory && !hasSupplements && !hasMonetaryObligations)
		return null;

	return (
		<div className={styles.additionalButtons}>
			{hasSupplements && (
				<Button
					type="button"
					kind="icon"
					size="medium"
					variant="ghost"
					onClick={openPicker}
					aria-label={t('product.pricing_supplements_button_aria')}
					title={t('product.pricing_supplements_button_aria')}
				>
					<Car />
				</Button>
			)}
			{hasMonetaryObligations && (
				<Button
					type="button"
					kind="icon"
					size="medium"
					variant="ghost"
					onClick={openObligations}
					aria-label={t('product.pricing_obligations_button_aria')}
					title={t('product.pricing_obligations_button_aria')}
				>
					<Documents />
				</Button>
			)}
			{hasPriceHistory && (
				<Button
					type="button"
					kind="icon"
					size="medium"
					variant="ghost"
					onClick={openPriceHistory}
					aria-label={t('product.pricing_history_button_aria')}
					title={t('product.pricing_history_button_aria')}
				>
					<History />
				</Button>
			)}
		</div>
	);
}

/**
 * SupplementsSection — renders forced + user-selected supplement items.
 *
 * Groups:
 *  - is_price_included: false, is_forced: true  → "Dodane części przynależne" (forced, no X)
 *  - is_price_included: false, is_forced: false → same section, with X (user-added)
 *  - is_price_included: true                    → "Inne koszty wyliczone w cenie"
 * 
 *  robocza notatka, grupowanie suplementów:
 *  jak są budowane FORCED? bo chcę móc dołączyć INNY produkt z oferty, i jednocześnie przygotować się na wliczenie/niewliczenie ceny w cenę lokalu (z CRM)
 *  1. produkty dołączone do oferty (is_forced && jaki check? czy ma crm_id_unit??) → "Oferta łączona, obejmuje również:"
 *    1a. produkty dołączone do oferty (is_price_included = true) → nie trzeba dodawać - już połączone ceną w CRM
 *    1b. produkty dołączone do oferty (is_price_included = false) → weź cenę produktu i zsumuj z produktem
 *  2. inne koszty oferty (is_forced && !crm_id_unit ?czy inaczej? chyba lepiej po kategriach) → "Inne koszty wyliczone w cenie"
 *    2a. koszty nie-produkty dołączone do oferty (is_price_included = true) → nie trzeba dodawać - już połączone ceną w CRM
 *    2b. koszty nie-produkty dołączone do oferty (is_price_included = false) → weź cenę kosztu i zsumuj z produktem
 *  3. produkty dodane przez użytkownika (is_forced = false) → "Dodane części przynależne", z X do usuwania, nie łączone ceną w CRM, ale pokazujące się w sumie
 *    - grupowane po kategoriach (parking, storage, inne)
 */
function SupplementsSection({ t, getProductsCategoryString, category, featureFlows, currency, locale }) {
	const { forcedItems, selectedItems, removeItem } = featureFlows.supplements;

	// const forcedProducts = forcedItems.filter((s) => s.category !== 'other' && s.crm_id_unit);
	// const forcedOtherCosts = forcedItems.filter((s) => s.category === 'other'); 

	const addedNonIncluded = forcedItems.filter((s) => !s.is_price_included);
	const addedIncluded = forcedItems.filter((s) => s.is_price_included);
	const userAdded = selectedItems;

	const hasAddedItems = addedNonIncluded.length > 0 || userAdded.length > 0;
	const hasIncludedItems = addedIncluded.length > 0;

	if (!hasAddedItems && !hasIncludedItems) return null;

	const LABEL_KEYS = {
		parking_indoor: 'product.supplements_label_parking',
		parking_outdoor: 'product.supplements_label_parking',
		storage_units: 'product.supplements_label_storage',
		apartment: 'product.supplements_label_apartment',
		commercial_unit: 'product.supplements_label_apartment',
		other: 'product.supplements_label_other',
	};

	const renderItem = (item, showRemove) => (
		<li key={item.id} className={styles.supplementItem}>
			{showRemove && (
				<button
					className={styles.supplementRemove}
					onClick={() => removeItem(item.id)}
					aria-label={t('product.supplements_remove_aria', { name: item.cms_product_name || item.name || item.crm_id_unit })}
				>
					<Close />
				</button>
			)}
			<span className={styles.supplementName}>{item.cms_product_name || item.name || item.crm_id_unit}</span>
			{item.price_brutto != null && (
				<span
					className={styles.supplementPrice}
					// title={
					// 	item.price_vat
					// 		? t('product.supplements_price_tooltip_vat', {
					// 				vat: item.price_vat,
					// 			})
					// 		: t('product.supplements_price_tooltip_no_vat')
					// }
				>
					{formatCurrency(item.price_brutto, { currency, locale, digits: 0 })}
				</span>
			)}
			{item.price_brutto == null && item.price_brutto_min != null && (
				<span
					className={styles.supplementPrice}
					// title={
					// 	item.price_vat
					// 		? t('product.supplements_price_tooltip_vat', {
					// 				vat: item.price_vat,
					// 			})
					// 		: t('product.supplements_price_tooltip_no_vat')
					// }
				>
					{formatCurrency(item.price_brutto_min, {
						currency,
						locale,
						digits: 0,
					})}
					{' – '}
					{formatCurrency(item.price_brutto_max, {
						currency,
						locale,
						digits: 0,
					})}
					</span>
				)}
			{ (item.price_brutto || item.price_brutto_min || item.price_brutto_max) && (
				<InfoTooltip
					text={
						item.price_vat
							? t('product.supplements_price_tooltip_vat', {
									vat: item.price_vat,
								})
							: t('product.supplements_price_tooltip_no_vat')
					}
					disableOnMobile
				/>
			)}
		</li>
	);

	const groupByCategory = (items) => {
		const groups = {};
		items.forEach((item) => {
			const category = item.category ?? 'other';
			if (!groups[category]) groups[category] = [];
			groups[category].push(item);
		});
		return groups;
	};

	const nonIncludedAll = [...addedNonIncluded, ...userAdded];
	const nonIncludedGroups = groupByCategory(nonIncludedAll);

	return (
		<>
		  {/*
			{hasForcedProducts && (
				<div className={styles.supplementsBlock}>
					<span className={styles.supplementsGroupLabel}>
						{t('product.supplements_forced_header')}
					</span>
					<ul className={styles.supplementList}>
						{forcedProducts.map((item) => renderItem(item, false))}
					</ul>
				</div>
			)}
			{hasForcedOtherCosts && (
				<div className={styles.supplementsBlock}>
					<span className={styles.supplementsGroupLabel}>
						{getProductsCategoryString('price_supplements_other_included', category)}
					</span>
					<ul className={styles.supplementList}>
						{forcedOtherCosts.map((item) => renderItem(item, false))}
					</ul>
				</div>
			)}
			*/}
			{hasIncludedItems && ( //usunąć
				<div className={styles.supplementsBlock}>
					<span className={styles.supplementsGroupLabel}>
						{t('product.supplements_included_header')}
					</span>
					<ul className={styles.supplementList}>
						{addedIncluded.map((item) => renderItem(item, false))}
					</ul>
				</div>
			)}
			{hasAddedItems && ( // zmienić w tylko user-added
				<div className={styles.supplementsBlock}>
					<span className={styles.supplementsGroupLabel}>
						{t('product.supplements_added_header')}
					</span>
					{Object.entries(nonIncludedGroups).map(([category, items]) => (
						<div key={category} className={styles.supplementTypeGroup}>
							<span className={styles.supplementTypeLabel}>
								{t(LABEL_KEYS[category] ?? LABEL_KEYS.other)}
							</span>
							<ul className={styles.supplementList}>
								{items.map((item) => renderItem(item, !item.is_forced))}
							</ul>
						</div>
					))}
				</div>
			)}

		</>
	);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * PricingPane — product price card: main prices, on-offer badge, Omnibus disclosure,
 * supplement table, and action icons (history / picker / obligations).
 *
 * Reads from ProductContext (selection, capabilities, featureFlows.supplements)
 * and AppContext (panelSettings for price visibility flags).
 *
 * totalAdditive (supplement sum) is available in featureFlows.supplements for
 * the ACTIONS pane to compute full grand total.
 */
export function PricingPane() {
	const { t } = useI18n();
	const getProductsCategoryString = useProductStrings();

	const { panelSettings, isMobile } = useApp();
	const { selection, capabilities, featureFlows } = useProduct();
	const { product } = selection;
	const { supplements, obligations, history } = featureFlows;

	const [isExpanded, setIsExpanded] = useState(
		() => !(panelSettings?.unit_sheet_pricing_pane?.start_collapsed ?? false),
	);

	const pricesVisibility = useMemo(() => parsePricesVisibility(panelSettings), [panelSettings]);

	const pricing = useMemo(() => {
		if (!product) return null;
		const currency = product.price_currency ?? 'PLN';
		const locale = currency === 'PLN' ? 'pl-PL' : 'en-US';
		return buildPricingData(product, { ...pricesVisibility, currency, locale });
	}, [product, pricesVisibility]);

	if (!product || !capabilities.hasPricing || !pricing) return null;

	const {
		isHidden,
		isOnOffer,
		pricePerSqm,
		strikethroughPricePerSqm,
		omnibusPricePerSqm,
		totalPrice,
		strikethroughPrice,
		omnibusPrice,
		vatNote,
		currency,
		locale,
	} = pricing;

	const hasSupplementsContent =
		supplements.forcedItems.length > 0 || supplements.selectedItems.length > 0;

	const showExpandToggle = !isHidden && (hasSupplementsContent || isOnOffer);

	const totalAdditive = supplements.totalAdditive;
	const grandTotal =
		pricing.resolvedPrice != null && totalAdditive > 0
			? formatCurrency(pricing.resolvedPrice + totalAdditive, {
					currency,
					locale,
				})
			: null;

	return (
		<div className={styles.pricingPaneHolder}>
			{/* On-offer badge */}
			{isOnOffer && !isHidden && (
				<div className={styles.offerBadge}>
					<span>{t('product.on_offer_badge')}</span>
				</div>
			)}

			<div 
				className={`${styles.pricingPane} ${isOnOffer ? styles.offer : ''}`}
			>
				{/* Header row */}
				<div className={styles.header}>
					<span className={styles.headerLabel}>
						{t('product.pricing_header')}
					</span>
					<div className={styles.headerActions}>
						<AdditionalInfoButtons
							capabilities={capabilities}
							featureFlows={featureFlows}
							t={t}
						/>
						{showExpandToggle && (
							<button
								className={`${styles.expandBtn} ${isExpanded ? '' : styles.expandBtn_collapsed}`}
								onClick={() => setIsExpanded((v) => !v)}
								aria-label={
									isExpanded
										? t('product.pricing_collapse_button_aria')
										: t('product.pricing_expand_button_aria')
								}
								style={{ 
									transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)', 
									transition: 'transform 0.25s ease' 
								}}
							>
								<ChevronUp />
							</button>
						)}
					</div>
				</div>

				{/* Price cells */}
				<div className={styles.priceRow}>
					{/* Price per sqm */}
					<div className={styles.priceCell}>
						<span className={styles.priceLabel}>
							{t('product.price_sqm')}
							{vatNote && (
								<span className={styles.vatNote}>
									{t('product.price_vat_note', { vat: vatNote })}
								</span>
							)}
						</span>
						<span className={styles.priceValue}>{pricePerSqm ?? totalPrice}</span>
						{isExpanded && isOnOffer && strikethroughPricePerSqm && (
							<span
								className={styles.strikethrough}
								aria-label={t('product.strikethrough_aria')}
							>
								{strikethroughPricePerSqm}
							</span>
						)}
						{isExpanded && isOnOffer && omnibusPricePerSqm && (
							<span className={styles.omnibusNote}>
								{t('product.omnibus_label')} {omnibusPricePerSqm}
							</span>
						)}
					</div>

					{/* Total price */}
					<div className={styles.priceCell}>
						<span className={styles.priceLabel}>
							{t('product.price_total')}
							{vatNote && (
								<span className={styles.vatNote}>
									{t('product.price_vat_note', { vat: vatNote })}
								</span>
							)}
						</span>
						<span className={styles.priceValue}>{totalPrice}</span>
						{isExpanded && isOnOffer && strikethroughPrice && (
							<span
								className={styles.strikethrough}
								aria-label={t('product.strikethrough_aria')}
							>
								{strikethroughPrice}
							</span>
						)}
						{isExpanded && isOnOffer && omnibusPrice && (
							<span className={styles.omnibusNote}>
								{t('product.omnibus_label')} {omnibusPrice}
							</span>
						)}
					</div>
				</div>

				{/* Supplements section */}
				{hasSupplementsContent && (
					<div
						className={`${styles.supplementsWrapper} ${isExpanded ? styles.supplementsWrapper_expanded : styles.supplementsWrapper_collapsed}`}
					>
						<SupplementsSection
							t={t}
							getProductsCategoryString={getProductsCategoryString}
							category={product.product_category}
							featureFlows={featureFlows}
							currency={currency}
							locale={locale}
						/>

						{/* Supplements subtotal */}
						{totalAdditive > 0 && (
							<div
								className={styles.supplementsTotal}
								onClick={() => setIsExpanded((v) => !v)}
							>
								<span className={styles.supplementsTotalLabel}>
									{t('product.supplements_total_label')}
								</span>
								<div className={styles.supplementsTotalValueBox}>
								<span className={styles.supplementsTotalValue}>
									{formatCurrency(totalAdditive, { currency, locale, digits: 0 })}
								</span>
								{capabilities.hasSupplements && (
									<Button
										key={'supplements_add'}
										type="button"
										kind="icon"
										size="medium"
										variant="ghost"
										onClick={(e) => { e.stopPropagation(); supplements.openPicker(); }}
										aria-label={t('product.pricing_supplements_button_aria')}
									>
										<AddToList />
									</Button>
								)}
								</div>
							</div>
						)}

					</div>
				)}

				{/* Grand total always visible */}
				{grandTotal && (
					<div
						className={`${styles.grandTotal} ${styles.grandTotal_standalone}`}
						onClick={() => setIsExpanded((v) => !v)}
					>
						<span className={styles.grandTotalLabel}>
							{getProductsCategoryString('price_total', product.product_category)}
						</span>
						<span className={styles.grandTotalValue}>{grandTotal}</span>
					</div>
				)}

				{/* Add optional purchase */}
				{capabilities.hasSupplements && !hasSupplementsContent && (
					<div className={styles.addOptionalPurchase}>
						<Button
							key={'supplements_add'}
							type="button"
							kind="wide"
							size="medium"
							variant="ghost"
							// selected={mode.id === activeMode}
							onClick={(e) => { e.stopPropagation(); supplements.openPicker(); }}
							// disabled={mode.isDisabled}
							// aria-pressed={mode.id === activeMode}
							aria-label={t('product.pricing_supplements_button_aria')}
							// title={t(mode.labelKey)}
						>
							<AddToList width="1.5em" height="1.5em" />
							{t('product.supplements_add_optional_purchase')}
						</Button>
					</div>
				)}

				{/* Modals */}
				{capabilities.hasSupplements && supplements.isPickerOpen && (
					<SupplementPickerModal
						isMobile={isMobile}
						isOpen={supplements.isPickerOpen}
						onClose={supplements.closePicker}
					/>
				)}
				{capabilities.hasMonetaryObligations && obligations.isObligationsOpen && 
					<MonetaryObligationsModal 
						isMobile={isMobile}
						isOpen={obligations.isObligationsOpen} 
						onClose={obligations.closeObligations} 
					/>}
				{capabilities.hasPriceHistory && history.isPriceHistoryOpen && (
					<PriceHistoryModal
						isMobile={isMobile}
						isOpen={history.isPriceHistoryOpen}
						onClose={history.closePriceHistory}
						pricesVisibility={pricesVisibility}
						pricing={pricing}
					/>
				)}
			</div>
		</div>
	);
}